import cx from "classnames";
import { ReactNode, useState } from "react";
import Button from "../../Element/Button";
import * as Typography from "../../Element/Typography";
import { Disclaimer } from "../../Element/Typography";
import { AlertIcon } from "../../Theme/assets/icons/commonIcons";
import styles from "./newsletter.module.css";
import {
  invalidEmailMessage,
  newsletterConsentText,
  newsletterTermsText,
} from "./utils";

const { SubHeading2, SectionTitle } = Typography.Heading;
const Label = Typography.Label;

export type NewsletterProps = {
  emailPlaceHolder: {
    label?: ReactNode;
    placeholder?: string;
  };
  backgroundImage: string;
  newsletterTitle?: ReactNode;
  newsletterBody: ReactNode;
  newsletterCtaText?: ReactNode;
  newsletterLang?: string;
  afterTextInput?: ReactNode;
  fieldValue?: string;
  value?: string;
  onSubmit: (
    email: string,
    isChecked: boolean,
  ) => Promise<{
    ok: boolean;
    message: string;
  }>;
  variant?: "standalone";
  buttonVariant?: "primary" | "secondary" | "tertiary";
  hasTermsInConsent?: boolean;
  signUpText?: string;
  subscriptionMessage?: {
    title: string;
    body: string;
  };
};

export default function Newsletter({
  emailPlaceHolder,
  newsletterTitle,
  newsletterBody,
  newsletterCtaText,
  newsletterLang,
  backgroundImage,
  afterTextInput,
  onSubmit,
  variant,
  buttonVariant,
  hasTermsInConsent,
  signUpText,
  subscriptionMessage,
}: NewsletterProps) {
  const [fieldValue, setFieldValue] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  const [error, setError] = useState("");
  const [serverMessage, setServerMessage] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setSubscribed] = useState(false);

  const validateEmail = (email: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setFieldValue(newEmail);
    if (!validateEmail(newEmail)) {
      setError(invalidEmailMessage(newsletterLang));
    } else {
      setError("");
    }
  };

  const handleFormSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setServerMessage("");

    if (!validateEmail(fieldValue)) {
      setIsDirty(true);
      setError(invalidEmailMessage(newsletterLang));
      return; // Don't submit if email is invalid
    }

    setIsSubmitting(true);
    try {
      const response = await onSubmit(fieldValue, isChecked);
      setServerMessage(response.message);
      if (response.ok) {
        setFieldValue("");
        setIsDirty(false);
        setSubscribed(true);
      } else {
        setIsDirty(true);
        setError(response.message);
      }
    } catch (e) {
      setIsDirty(true);
      setError("An unexpected error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const showSubscribedView = !!(
    isSubscribed &&
    subscriptionMessage?.title &&
    subscriptionMessage?.body
  );

  return (
    <div className={cx(styles.newsletterContainer, variant && styles[variant])}>
      {/* Background image is positioned absolute */}
      {backgroundImage && (
        <img
          src={backgroundImage}
          alt=""
          className={styles.newsletterImage}
          loading={
            import.meta.env.VITE_SKIP_LAZY || variant === "standalone"
              ? undefined
              : "lazy"
          }
        />
      )}

      <div
        className={cx(
          styles.contentContainer,
          showSubscribedView && styles.isSubscribed,
        )}
      >
        {showSubscribedView ? (
          <div
            className={cx(
              styles.subscribedContainer,
              variant === "standalone" && styles.standalone,
            )}
          >
            <div className={styles.title}>{subscriptionMessage?.title}</div>
            <div className={styles.message}>{subscriptionMessage?.body}</div>
          </div>
        ) : (
          <>
            {newsletterTitle && (
              <div className={styles.newsletterTitle}>
                {variant === "standalone" ? (
                  <SectionTitle>{newsletterTitle}</SectionTitle>
                ) : (
                  <SubHeading2>{newsletterTitle}</SubHeading2>
                )}
              </div>
            )}
            {newsletterBody && (
              <div className={styles.newsletterBody}>
                <p>{newsletterBody}</p>
              </div>
            )}
            <div className={styles.formContainer}>
              {emailPlaceHolder.label && (
                <div>
                  <label>
                    <Label>{emailPlaceHolder.label}</Label>
                  </label>
                </div>
              )}
              <div className={styles.inputContainer}>
                <form
                  className={styles.inputForm}
                  noValidate
                  onSubmit={handleFormSubmit}
                >
                  <div className={styles.inputButtonContainer}>
                    <div className={cx(styles.formGroup)}>
                      {/* grid-area: email; */}
                      <div className={cx(styles.emailInput)}>
                        <input
                          placeholder={emailPlaceHolder.placeholder}
                          value={fieldValue}
                          onChange={handleEmailChange}
                          type="email"
                        />
                        {error && isDirty && (
                          <div className={styles.errorMessage}>
                            <Disclaimer big>
                              {AlertIcon} {error}
                            </Disclaimer>
                          </div>
                        )}
                      </div>
                      {/* grid-area: checkbox; */}
                      <label className={styles.checkboxContainer}>
                        <input
                          type="checkbox"
                          name="marketingCheckbox"
                          checked={isChecked}
                          onChange={(e) => {
                            setIsChecked(e.target.checked);
                          }}
                        />
                        {newsletterConsentText({
                          lang: newsletterLang,
                          className: styles.tncLink,
                          hasTermsInConsent,
                          signUpText,
                        })}
                      </label>
                      {/* grid-area: button; */}
                      <span className={styles.signupButton}>
                        <Button
                          variant={buttonVariant || "secondary"}
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {newsletterCtaText}
                        </Button>
                      </span>
                      {serverMessage && !isDirty && (
                        <Disclaimer>{serverMessage}</Disclaimer>
                      )}
                    </div>
                    {afterTextInput && (
                      <div className={styles.afterTextInput}>
                        <p>{afterTextInput}</p>
                      </div>
                    )}
                    <p className={styles.termsAndConditions}>
                      {newsletterTermsText({
                        lang: newsletterLang,
                        className: styles.tncLink,
                        hasTermsInConsent,
                        signUpText,
                      })}
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
